<template>
  <div class="proyectos-nuevo">
    <!-- Header -->
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Nuevo Proyecto</h1>
        <p class="content-header-subtitle">Crear un nuevo proyecto en el sistema</p>
      </div>
      <div class="content-header-right">
        <b-button variant="outline-secondary" @click="volver">
          <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
          Volver
        </b-button>
      </div>
    </div>


    <!-- Formulario -->
    <b-card>
      <b-form @submit.prevent="guardarProyecto">
        <b-row>
          <b-col md="12">
            <b-form-group label="Nombre del Proyecto" label-for="nombre" required>
              <b-form-input
                id="nombre"
                v-model="proyecto.nombre"
                type="text"
                placeholder="Ingrese el nombre del proyecto"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Fecha de Inicio" label-for="fechaInicio" required>
              <b-form-datepicker
                id="fechaInicio"
                v-model="proyecto.fechaInicio"
                :start-weekday="1"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Fecha de Fin Estimada" label-for="fechaFin">
              <b-form-datepicker
                id="fechaFin"
                v-model="proyecto.fechaFin"
                :start-weekday="1"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Estado" label-for="estado" required>
              <b-form-select
                id="estado"
                v-model="proyecto.estado"
                :options="opcionesEstado"
                placeholder="Seleccione un estado"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Descripción" label-for="descripcion">
          <b-form-textarea
            id="descripcion"
            v-model="proyecto.descripcion"
            rows="4"
            placeholder="Descripción detallada del proyecto..."
          />
        </b-form-group>

        <b-row>
          <b-col md="6">
            <b-form-group label="Presupuesto" label-for="presupuesto">
              <b-input-group>
                <b-input-group-prepend is-text>€</b-input-group-prepend>
                <b-form-input
                  id="presupuesto"
                  v-model="proyecto.presupuesto"
                  type="number"
                  placeholder="0.00"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Responsable" label-for="responsable">
              <b-form-select
                id="responsable"
                v-model="proyecto.responsable"
                :options="opcionesResponsables"
              />
            </b-form-group>
          </b-col>
        </b-row>

      </b-form>
    </b-card>

    <!-- Sección de Obras - Acordeón -->
    <b-card class="mt-4">
      <div class="accordion" role="tablist">
        <b-card no-body class="mb-1">
          <b-card-header header-tag="header" class="p-1" role="tab">
            <b-button 
              block 
              v-b-toggle.accordion-obras 
              variant="outline-primary"
              class="text-left d-flex justify-content-between align-items-center"
            >
              <div class="d-flex align-items-center">
                <feather-icon icon="PackageIcon" size="18" class="mr-2" />
                <span>Obras del Proyecto</span>
                <b-badge v-if="proyecto.obras && proyecto.obras.length > 0" variant="primary" class="ml-2">
                  {{ proyecto.obras.length }}
                </b-badge>
              </div>
              <b-button 
                variant="primary" 
                size="sm" 
                @click.stop="mostrarModalObra"
                class="mr-3"
              >
                <feather-icon icon="PlusIcon" size="16" class="mr-50" />
                Agregar Obra
              </b-button>
            </b-button>
          </b-card-header>
          <b-collapse id="accordion-obras" accordion="my-accordion" role="tabpanel">
            <b-card-body>
              <div v-if="proyecto.obras && proyecto.obras.length > 0">
                <b-table
                  :items="proyecto.obras"
                  :fields="camposObras"
                  striped
                  hover
                  responsive
                >
                  <template #cell(direccion)="data">
                    <div class="text-truncate" style="max-width: 200px;" :title="data.item.direccion">
                      {{ data.item.direccion }}
                    </div>
                  </template>
                  
                  <template #cell(municipio)="data">
                    <div>
                      <strong>{{ data.item.municipio }}</strong><br>
                      <small class="text-muted">{{ data.item.provincia }}</small>
                    </div>
                  </template>
                  
                  <template #cell(tipoObra)="data">
                    <b-badge variant="info">
                      {{ data.item.nombreTipoObra || 'Sin tipo' }}
                    </b-badge>
                  </template>
                  
                  <template #cell(estadoObra)="data">
                    <b-badge :variant="getObraBadgeVariant(data.item.nombreEstadoObra || '')">
                      {{ data.item.nombreEstadoObra || 'Sin estado' }}
                    </b-badge>
                  </template>
                  
                  <template #cell(fase)="data">
                    <b-badge variant="secondary">
                      {{ data.item.nombreFase || 'Sin fase' }}
                    </b-badge>
                  </template>
                  
                  <template #cell(fechas)="data">
                    <div class="text-center">
                      <div><strong>Inicio:</strong> {{ formatearFecha(data.item.fechaInicioObra) }}</div>
                      <div><strong>Fin:</strong> {{ formatearFecha(data.item.fechaFinObra) }}</div>
                    </div>
                  </template>
                  
                  <template #cell(empresa)="data">
                    <div class="text-truncate" style="max-width: 150px;" :title="data.item.nombreEmpresa">
                      {{ data.item.nombreEmpresa || 'Sin empresa' }}
                    </div>
                  </template>
                  
                  <template #cell(acciones)="row">
                    <b-button-group size="sm">
                      <b-button variant="outline-primary" @click="editarObra(row.item, row.index)">
                        <feather-icon icon="EditIcon" size="14" />
                      </b-button>
                      <b-button variant="outline-danger" @click="eliminarObra(row.index)">
                        <feather-icon icon="TrashIcon" size="14" />
                      </b-button>
                    </b-button-group>
                  </template>
                </b-table>
              </div>
              <div v-else class="text-center text-muted py-4">
                <feather-icon icon="PackageIcon" size="48" class="mb-2" />
                <p>No hay obras agregadas al proyecto</p>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
    </b-card>

    <!-- Sección de Actividades - Acordeón -->
    <b-card class="mt-4">
      <div class="accordion" role="tablist">
        <b-card no-body class="mb-1">
          <b-card-header header-tag="header" class="p-1" role="tab">
            <b-button 
              block 
              v-b-toggle.accordion-actividades 
              variant="outline-success"
              class="text-left d-flex justify-content-between align-items-center"
            >
              <div class="d-flex align-items-center">
                <feather-icon icon="ActivityIcon" size="18" class="mr-2" />
                <span>Historial de Actividades</span>
                <b-badge v-if="proyecto.actividades && proyecto.actividades.length > 0" variant="success" class="ml-2">
                  {{ proyecto.actividades.length }}
                </b-badge>
              </div>
              <b-button 
                variant="success" 
                size="sm" 
                @click.stop="mostrarModalActividad"
                class="mr-3"
              >
                <feather-icon icon="PlusIcon" size="16" class="mr-50" />
                Nueva Actividad
              </b-button>
            </b-button>
          </b-card-header>
          <b-collapse id="accordion-actividades" accordion="my-accordion" role="tabpanel">
            <b-card-body>
              <div v-if="proyecto.actividades && proyecto.actividades.length > 0">
                <div class="timeline">
                  <div
                    v-for="(actividad, index) in proyecto.actividades"
                    :key="index"
                    class="timeline-item"
                  >
                    <div class="timeline-marker">
                      <feather-icon :icon="getActividadIcon(actividad.tipo)" size="16" />
                    </div>
                    <div class="timeline-content">
                      <div class="timeline-header d-flex justify-content-between align-items-start">
                        <div>
                          <h6 class="mb-1">{{ actividad.titulo }}</h6>
                          <small class="text-muted">{{ formatearFecha(actividad.fecha) }}</small>
                        </div>
                        <div class="timeline-actions">
                          <b-button
                            variant="outline-primary"
                            size="sm"
                            @click="editarActividad(index)"
                            class="mr-1"
                          >
                            <feather-icon icon="EditIcon" size="14" />
                          </b-button>
                          <b-button
                            variant="outline-danger"
                            size="sm"
                            @click="eliminarActividad(index)"
                          >
                            <feather-icon icon="TrashIcon" size="14" />
                          </b-button>
                        </div>
                      </div>
                      <p class="mb-1">{{ actividad.descripcion }}</p>
                      <div class="timeline-meta">
                        <b-badge :variant="getActividadBadgeVariant(actividad.tipo)">
                          {{ actividad.tipo }}
                        </b-badge>
                        <span v-if="actividad.usuario" class="ml-2">
                          por {{ actividad.usuario }}
                        </span>
                        <span v-if="actividad.documentos && actividad.documentos.length > 0" class="ml-2">
                          <feather-icon icon="PaperclipIcon" size="14" />
                          {{ actividad.documentos.length }} documento(s)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-center text-muted py-4">
                <feather-icon icon="ActivityIcon" size="48" class="mb-2" />
                <p>No hay actividades registradas</p>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
    </b-card>

    <!-- Modal para Agregar/Editar Obra -->
    <b-modal
      id="modal-obra"
      :title="modalObra.editing ? 'Editar Obra' : 'Gestionar Obras'"
      size="lg"
      @hidden="resetModalObra"
    >
      <b-tabs v-model="modalObra.activeTab" content-class="mt-3">
        <!-- Pestaña: Seleccionar Obras Existentes -->
        <b-tab title="Seleccionar Obras" :title-link-class="modalObra.activeTab === 0 ? 'active' : ''">
          <div class="mb-3">
            <b-form-input
              v-model="filtroObras"
              placeholder="Buscar obras..."
              type="search"
            />
          </div>
          
          <div v-if="obrasDisponibles.length > 0" class="obras-list">
            <div
              v-for="(obra, index) in obrasFiltradas"
              :key="index"
              class="obra-item"
              :class="{ 'selected': obraSeleccionada && obraSeleccionada.idObra === obra.idObra }"
              @click="seleccionarObra(obra)"
            >
              <div class="obra-content">
                <div class="obra-header">
                  <h6 class="mb-1">{{ obra.direccion }}</h6>
                  <b-badge :variant="getObraBadgeVariant(obra.nombreEstadoObra)">
                    {{ obra.nombreEstadoObra }}
                  </b-badge>
                </div>
                <p class="obra-descripcion mb-1">{{ obra.descripcion || 'Sin descripción' }}</p>
                <div class="obra-meta">
                  <span v-if="obra.municipio" class="text-muted">
                    <feather-icon icon="MapPinIcon" size="14" class="mr-1" />
                    {{ obra.municipio }}, {{ obra.provincia }}
                  </span>
                  <span v-if="obra.fechaInicioObra" class="text-muted ml-3">
                    <feather-icon icon="CalendarIcon" size="14" class="mr-1" />
                    {{ formatearFecha(obra.fechaInicioObra) }}
                  </span>
                </div>
              </div>
              <div class="obra-actions">
                <feather-icon 
                  v-if="obraSeleccionada && obraSeleccionada.idObra === obra.idObra"
                  icon="CheckIcon" 
                  size="20" 
                  class="text-success"
                />
                <feather-icon 
                  v-else
                  icon="PlusIcon" 
                  size="20" 
                  class="text-muted"
                />
              </div>
            </div>
          </div>
          <div v-else class="text-center text-muted py-4">
            <feather-icon icon="PackageIcon" size="48" class="mb-2" />
            <p>No hay obras disponibles</p>
          </div>
        </b-tab>

        <!-- Pestaña: Crear Nueva Obra -->
        <b-tab title="Crear Nueva Obra" :title-link-class="modalObra.activeTab === 1 ? 'active' : ''">
          <b-form @submit.prevent="guardarObra">
            <b-row>
              <!-- Información básica -->
              <b-col md="6">
                <h6 class="section-title">Información Básica</h6>
                
                <b-form-group label="Dirección" required>
                  <b-form-input 
                    v-model="modalObra.direccion" 
                    placeholder="Ingrese la dirección de la obra"
                    required
                    :state="erroresObra.direccion"
                    @input="erroresObra.direccion = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.direccion === false">
                    La dirección es requerida
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Referencia Externa">
                  <b-form-input 
                    v-model="modalObra.referenciaExterna" 
                    placeholder="Referencia externa (opcional)"
                  />
                </b-form-group>

                <b-form-group label="Referencia de Obra">
                  <b-form-select 
                    v-model="modalObra.idReferenciaObra" 
                    :options="referenciasObras"
                    value-field="idReferenciaObra"
                    text-field="nombre"
                    placeholder="Seleccione una referencia"
                  />
                </b-form-group>

                <b-form-group label="Fase" required>
                  <b-form-select 
                    v-model="modalObra.idFase" 
                    :options="fases"
                    value-field="idFase"
                    text-field="nombre"
                    placeholder="Seleccione una fase"
                    required
                    :state="erroresObra.idFase"
                    @change="erroresObra.idFase = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.idFase === false">
                    La fase es requerida
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Tipo de Obra" required>
                  <b-form-select 
                    v-model="modalObra.idTipoObra" 
                    :options="tiposObras"
                    value-field="idTipoObra"
                    text-field="nombre"
                    placeholder="Seleccione un tipo de obra"
                    required
                    :state="erroresObra.idTipoObra"
                    @change="erroresObra.idTipoObra = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.idTipoObra === false">
                    El tipo de obra es requerido
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Estado de Obra" required>
                  <b-form-select 
                    v-model="modalObra.idEstadoObra" 
                    :options="estadosObras"
                    value-field="idEstadoObra"
                    text-field="nombre"
                    placeholder="Seleccione un estado"
                    required
                    :state="erroresObra.idEstadoObra"
                    @change="erroresObra.idEstadoObra = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.idEstadoObra === false">
                    El estado de obra es requerido
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>

              <!-- Ubicación -->
              <b-col md="6">
                <h6 class="section-title">Ubicación</h6>
                
                <b-form-group label="Municipio" required>
                  <b-form-input 
                    v-model="modalObra.municipio" 
                    placeholder="Ingrese el municipio"
                    required
                    :state="erroresObra.municipio"
                    @input="erroresObra.municipio = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.municipio === false">
                    El municipio es requerido
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Provincia" required>
                  <b-form-input 
                    v-model="modalObra.provincia" 
                    placeholder="Ingrese la provincia"
                    required
                    :state="erroresObra.provincia"
                    @input="erroresObra.provincia = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.provincia === false">
                    La provincia es requerida
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Código Postal" required>
                  <b-form-input 
                    v-model="modalObra.codigoPostal" 
                    type="number"
                    placeholder="Ingrese el código postal"
                    required
                    :state="erroresObra.codigoPostal"
                    @input="erroresObra.codigoPostal = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.codigoPostal === false">
                    El código postal es requerido
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Empresa" required>
                  <div class="d-flex gap-2">
                    <b-form-select 
                      v-model="modalObra.idEmpresa" 
                      :options="empresas"
                      value-field="idEmpresa"
                      text-field="nombre"
                      placeholder="Seleccione una empresa"
                      required
                      class="flex-grow-1"
                      :state="erroresObra.idEmpresa"
                      @change="erroresObra.idEmpresa = null"
                    />
                    <b-button 
                      variant="outline-primary" 
                      size="sm"
                      @click="mostrarModalEmpresa"
                      title="Crear nueva empresa"
                    >
                      <feather-icon icon="PlusIcon" size="16" />
                    </b-button>
                  </div>
                  <b-form-invalid-feedback v-if="erroresObra.idEmpresa === false">
                    La empresa es requerida
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <!-- Fechas -->
              <b-col md="6">
                <h6 class="section-title">Fechas</h6>
                
                <b-form-group label="Fecha de Inicio" required>
                  <b-form-input 
                    v-model="modalObra.fechaInicioObra" 
                    type="date"
                    required
                    :state="erroresObra.fechaInicioObra"
                    @input="erroresObra.fechaInicioObra = null"
                  />
                  <b-form-invalid-feedback v-if="erroresObra.fechaInicioObra === false">
                    La fecha de inicio es requerida
                  </b-form-invalid-feedback>
                </b-form-group>

                <b-form-group label="Fecha de Fin">
                  <b-form-input 
                    v-model="modalObra.fechaFinObra" 
                    type="date"
                  />
                </b-form-group>
              </b-col>

              <!-- Información adicional -->
              <b-col md="6">
                <h6 class="section-title">Información Adicional</h6>
                
                <b-form-group label="Magnitudes">
                  <b-form-textarea 
                    v-model="modalObra.magnitudes" 
                    placeholder="Descripción de las magnitudes de la obra"
                    rows="3"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col>
                <h6 class="section-title">Descripción</h6>
                <b-form-group label="Descripción de la Obra">
                  <b-form-textarea 
                    v-model="modalObra.descripcion" 
                    placeholder="Descripción detallada de la obra"
                    rows="4"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Botón de importar desde Construdata -->
            <b-row>
              <b-col>
                <div class="d-flex justify-content-start mt-3">
                  <b-button 
                    variant="outline-info" 
                    @click="mostrarModalImportarConstrudata"
                    size="sm"
                  >
                    <feather-icon icon="DownloadIcon" size="16" class="mr-1" />
                    Importar desde Construdata
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </b-form>
        </b-tab>
      </b-tabs>
      
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="$bvModal.hide('modal-obra')">
          Cancelar
        </b-button>
        <b-button 
          v-if="modalObra.activeTab === 0 && obraSeleccionada"
          variant="primary" 
          @click="agregarObraSeleccionada"
        >
          Agregar Obra Seleccionada
        </b-button>
        <b-button 
          v-else-if="modalObra.activeTab === 1"
          variant="success" 
          @click="guardarObra"
        >
          {{ modalObra.editing ? 'Actualizar' : 'Crear' }} Obra
        </b-button>
      </template>
    </b-modal>

    <!-- Modal para importar desde Construdata -->
    <b-modal
      id="modal-importar-construdata"
      ref="modalImportarConstrudata"
      title="Importar desde Construdata"
      size="lg"
      @hidden="limpiarFormularioConstrudata"
      @ok="importarDatosConstrudata"
      :ok-disabled="!textoConstrudata.trim() || procesandoConstrudata"
    >
      <div class="importar-construdata">
        <p class="text-muted mb-3">
          Pega aquí el texto copiado desde Construdata para importar automáticamente los datos de la obra.
        </p>
        
        <b-form-group label="Texto de Construdata" required>
          <b-form-textarea 
            v-model="textoConstrudata" 
            placeholder="Pega aquí el texto de Construdata..."
            rows="12"
            required 
            :state="textoConstrudata.trim() ? true : null"
          />
          <b-form-text class="mt-2">
            <small>Ejemplo de formato esperado:</small><br>
            <code class="text-muted">
              Entrelux III	Ref.: 417-1918<br>
              DIRECCIÓN<br>
              Dos Hermanas (41700) Sevilla, Andalucía, ESPAÑA<br>
              TIPO DE OBRA<br>
              Instalaciones Deportivas / Viviendas Unifamiliares<br>
              ...
            </code>
          </b-form-text>
        </b-form-group>

        <div v-if="datosParseados" class="mt-3">
          <h6>Datos detectados:</h6>
          <div class="bg-light p-3 rounded">
            <div v-if="datosParseados.nombre" class="mb-1">
              <strong>Nombre:</strong> {{ datosParseados.nombre }}
            </div>
            <div v-if="datosParseados.referencia" class="mb-1">
              <strong>Referencia:</strong> {{ datosParseados.referencia }}
            </div>
            <div v-if="datosParseados.direccion" class="mb-1">
              <strong>Dirección:</strong> {{ datosParseados.direccion }}
            </div>
            <div v-if="datosParseados.municipio" class="mb-1">
              <strong>Ubicación:</strong> {{ datosParseados.municipio }}, {{ datosParseados.provincia }} ({{ datosParseados.codigoPostal }})
            </div>
            <div v-if="datosParseados.promotor" class="mb-1">
              <strong>Promotor:</strong> {{ datosParseados.promotor }}
            </div>
            <div v-if="datosParseados.descripcion" class="mb-1">
              <strong>Descripción:</strong> {{ datosParseados.descripcion.substring(0, 100) }}{{ datosParseados.descripcion.length > 100 ? '...' : '' }}
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="cancel">
          Cancelar
        </b-button>
        <b-button 
          variant="primary" 
          @click="ok"
          :disabled="!textoConstrudata.trim() || procesandoConstrudata"
        >
          <b-spinner v-if="procesandoConstrudata" small class="mr-1" />
          {{ procesandoConstrudata ? 'Procesando...' : 'Importar Datos' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Modal para Agregar Actividad -->
    <b-modal
      id="modal-actividad"
      :title="editandoActividad ? 'Editar Actividad' : 'Nueva Actividad'"
      size="lg"
      @hidden="resetModalActividad"
    >
      <b-form @submit.prevent="guardarActividad">
        <b-row>
          <b-col md="6">
            <b-form-group label="Tipo de Actividad" required>
              <b-form-select v-model="modalActividad.tipoActividad" :options="opcionesTipoActividad" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Fecha" required>
              <b-form-datepicker 
                v-model="modalActividad.fecha" 
                :start-weekday="1"
                required 
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Estado de Actividad" required>
              <b-form-select v-model="modalActividad.estadoActividad" :options="opcionesEstadoActividad" required />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Próxima Acción Fecha" required>
              <b-form-datepicker 
                v-model="modalActividad.proximaAccionFecha" 
                :start-weekday="1"
                required 
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Próxima Acción Tipo" required>
              <b-form-select v-model="modalActividad.proximaAccionTipo" :options="opcionesAccionActividad" required />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Recordatorio">
              <b-form-checkbox v-model="modalActividad.recordatorio" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Observaciones">
          <b-form-textarea v-model="modalActividad.observaciones" rows="4" />
        </b-form-group>

        <!-- Sección de adjuntar documentos -->
        <div class="mt-4">
          <hr>
          <h6 class="mb-3">
            <feather-icon icon="PaperclipIcon" size="16" class="mr-1" />
            Adjuntar Documentos (Opcional)
          </h6>
          
          <div class="documentos-container">
            <div v-for="(documento, index) in documentosActividad" :key="index" class="documento-item mb-3">
              <b-card class="p-3">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{{ documento.nombreArchivo }}</strong>
                    <br>
                    <small class="text-muted">
                      Categoría: {{ documento.nombreCategoria }} | 
                      Versión: {{ documento.version }} | 
                      Tamaño: {{ (documento.tamaño / 1024).toFixed(1) }} KB
                    </small>
                  </div>
                  <b-button-group size="sm">
                    <b-button variant="outline-primary" @click="descargarDocumento(index)" title="Descargar documento">
                      <feather-icon icon="DownloadIcon" size="14" />
                    </b-button>
                    <b-button variant="outline-danger" @click="eliminarDocumento(index)" title="Eliminar documento">
                      <feather-icon icon="TrashIcon" size="14" />
                    </b-button>
                  </b-button-group>
                </div>
              </b-card>
            </div>
            
            <div v-if="documentosActividad.length === 0" class="text-center text-muted py-3">
              <feather-icon icon="FileIcon" size="24" class="mb-2" />
              <p>No hay documentos adjuntos</p>
            </div>
            
            <b-button variant="outline-primary" @click="mostrarSubidaDocumentos = true">
              <feather-icon icon="PlusIcon" size="16" class="mr-1" />
              Agregar Documento
            </b-button>
          </div>
        </div>

        <!-- Modal para subir documentos -->
        <b-modal
          v-model="mostrarSubidaDocumentos"
          title="Subir Documento"
          size="lg"
          @hidden="resetSubidaDocumentos"
        >
          <SubidaDocumentos 
            :id-actividad="null"
            :id-usuario="usuarioActual.idUsuario"
            :modo-temporal="true"
            @documento-subido="onDocumentoSubido"
            @finalizar-subida="onFinalizarSubidaDocumentos"
          />
        </b-modal>
      </b-form>
      
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="$bvModal.hide('modal-actividad')">
          Cancelar
        </b-button>
        <b-button 
          variant="success" 
          @click="guardarActividadConDocumentos"
          :disabled="guardandoActividad"
        >
          <feather-icon
            v-if="guardandoActividad"
            icon="LoaderIcon"
            size="16"
            class="mr-50"
            animation="spin"
          />
          {{ guardandoActividad ? 'Guardando...' : 'Guardar Actividad' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Modal para Crear Nueva Empresa -->
    <b-modal
      id="modal-empresa"
      title="Nueva Empresa"
      size="lg"
      @hidden="resetModalEmpresa"
    >
      <b-form @submit.prevent="guardarEmpresa">
        <b-row>
          <b-col md="6">
            <b-form-group label="Nombre de la Empresa" required>
              <b-form-input 
                v-model="modalEmpresa.nombre" 
                placeholder="Ingrese el nombre de la empresa"
                required 
              />
            </b-form-group>

            <b-form-group label="CIF" required>
              <b-form-input 
                v-model="modalEmpresa.cif" 
                placeholder="Ingrese el CIF"
                required 
              />
            </b-form-group>

            <b-form-group label="Tipo de Empresa" required>
              <b-form-select 
                v-model="modalEmpresa.idTipoEmpresa" 
                :options="tiposEmpresas"
                value-field="idTipoEmpresa"
                text-field="nombre"
                placeholder="Seleccione un tipo"
                required
              />
            </b-form-group>

            <b-form-group label="Teléfono">
              <b-form-input 
                v-model="modalEmpresa.telefono" 
                placeholder="Ingrese el teléfono"
              />
            </b-form-group>

            <b-form-group label="Email">
              <b-form-input 
                v-model="modalEmpresa.email" 
                type="email"
                placeholder="Ingrese el email"
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Dirección" required>
              <b-form-textarea 
                v-model="modalEmpresa.direccion" 
                placeholder="Ingrese la dirección completa"
                rows="3"
                required 
              />
            </b-form-group>

            <b-form-group label="Sitio Web">
              <b-form-input 
                v-model="modalEmpresa.web" 
                placeholder="https://www.ejemplo.com"
              />
            </b-form-group>

            <b-form-group label="Observaciones">
              <b-form-textarea 
                v-model="modalEmpresa.observaciones" 
                placeholder="Observaciones adicionales"
                rows="3"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="$bvModal.hide('modal-empresa')">
          Cancelar
        </b-button>
        <b-button variant="success" @click="guardarEmpresa" :disabled="guardandoEmpresa">
          <feather-icon
            v-if="guardandoEmpresa"
            icon="LoaderIcon"
            size="16"
            class="mr-50"
            animation="spin"
          />
          {{ guardandoEmpresa ? 'Guardando...' : 'Crear Empresa' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Botones al Final de la Página -->
    <div class="d-flex justify-content-end gap-2 mt-4">
      <b-button variant="outline-secondary" @click="volver">
        Cancelar
      </b-button>
      <b-button variant="primary" :disabled="guardando" @click="guardarProyecto">
        <feather-icon
          v-if="guardando"
          icon="LoaderIcon"
          size="16"
          class="mr-50"
          animation="spin"
        />
        <feather-icon
          v-else
          icon="SaveIcon"
          size="16"
          class="mr-50"
        />
        {{ guardando ? 'Guardando...' : 'Guardar Proyecto' }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { EstadoProyectoService } from '@/services/estadoProyectoService'
import { EmpresaService } from '@/services/empresaService'
import { ObraService } from '@/services/obraService'
import { TipoEmpresaService } from '@/services/tipoEmpresaService'
import { ReferenciaObraService } from '@/services/referenciaObraService'
import { FaseService } from '@/services/faseService'
import { TipoObraService } from '@/services/tipoObraService'
import { EstadoObraService } from '@/services/estadoObraService'
import { ProyectoService } from '@/services/proyectoService'
import { TipoActividadService } from '@/services/tipoActividadService'
import { EstadoActividadService } from '@/services/estadoActividadService'
import { AccionActividadService } from '@/services/accionActividadService'
import usuarioService from '@/services/usuarioService'
import SubidaDocumentos from '@/components/SubidaDocumentos.vue'
import authService from '@/services/authService'
import { DocumentoService } from '@/services/documentoService'
import { 
  generarEnlaceGoogleCalendar, 
  generarEnlaceOutlookCalendar, 
  descargarArchivoICS,
  crearEventoDesdeActividad 
} from '@/utils/calendarUtils'

export default {
  name: 'ProyectosNuevo',
  components: {
    SubidaDocumentos
  },
  data() {
    return {
      guardando: false,
      guardandoEmpresa: false,
      proyecto: {
        nombre: '',
        fechaInicio: '',
        fechaFin: '',
        presupuesto: '',
        estado: null, // Sin selección por defecto
        descripcion: '',
        responsable: '',
        obras: [],
        actividades: [],
      },
      opcionesEstado: [],
      estadosProyectos: [],
      opcionesResponsables: [
        { value: '', text: 'Seleccionar responsable' },
      ],
      usuarios: [],
      // Campos para la tabla de obras
      camposObras: [
        { key: 'direccion', label: 'Dirección', sortable: true },
        { key: 'municipio', label: 'Ubicación', sortable: true },
        { key: 'tipoObra', label: 'Tipo', sortable: true },
        { key: 'estadoObra', label: 'Estado', sortable: true },
        { key: 'fase', label: 'Fase', sortable: true },
        { key: 'fechas', label: 'Fechas', sortable: false },
        { key: 'empresa', label: 'Empresa', sortable: true },
        { key: 'acciones', label: 'Acciones', sortable: false },
      ],
      // Opciones para el estado de obras
      opcionesEstadoObra: [
        { value: 'Pendiente', text: 'Pendiente' },
        { value: 'En Progreso', text: 'En Progreso' },
        { value: 'Completada', text: 'Completada' },
        { value: 'Cancelada', text: 'Cancelada' },
      ],
      // Opciones para tipos de actividad (se cargarán desde el backend)
      opcionesTipoActividad: [
        { value: null, text: 'Seleccionar tipo de actividad' }
      ],
      opcionesEstadoActividad: [
        { value: null, text: 'Seleccionar estado' }
      ],
      opcionesAccionActividad: [
        { value: null, text: 'Seleccionar acción' }
      ],
      // Modal para obras
      modalObra: {
        editing: false,
        index: -1,
        activeTab: 0,
        // Información básica
        direccion: '',
        referenciaExterna: '',
        idReferenciaObra: '',
        idFase: '',
        idTipoObra: '',
        idEstadoObra: '',
        // Ubicación
        municipio: '',
        provincia: '',
        codigoPostal: '',
        idEmpresa: '',
        // Fechas
        fechaInicioObra: '',
        fechaFinObra: '',
        // Información adicional
        magnitudes: '',
        descripcion: '',
      },
      // Errores de validación para el formulario de obra
      erroresObra: {
        direccion: null,
        idFase: null,
        idTipoObra: null,
        idEstadoObra: null,
        municipio: null,
        provincia: null,
        codigoPostal: null,
        idEmpresa: null,
        fechaInicioObra: null,
      },
      // Obras disponibles para seleccionar (cargadas desde backend)
      obrasDisponibles: [],
      filtroObras: '',
      obraSeleccionada: null,
      // Opciones para los campos select (cargadas desde backend)
      referenciasObras: [],
      fases: [],
      tiposObras: [],
      estadosObras: [],
      empresas: [],
      tiposEmpresas: [],
      // Modal para actividades
      modalActividad: {
        tipoActividad: null,
        fecha: '',
        estadoActividad: null,
        observaciones: '',
        proximaAccionFecha: '',
        proximaAccionTipo: null,
        recordatorio: false,
      },
      // Variables para la subida de documentos en actividades
      mostrarSubidaDocumentos: false,
      actividadTemporalId: null,
      guardandoActividad: false,
      documentosActividad: [], // Array para almacenar documentos temporalmente
      // Variables para edición de actividades
      editandoActividad: false,
      indiceActividadEditando: null,
      usuarioActual: {
        idUsuario: null
      },
      // Modal para empresas
      modalEmpresa: {
        nombre: '',
        direccion: '',
        idTipoEmpresa: '',
        cif: '',
        web: '',
        telefono: '',
        email: '',
        observaciones: '',
      },
      // Datos para importar desde Construdata
      textoConstrudata: '',
      datosParseados: null,
      procesandoConstrudata: false
    }
  },
  computed: {
    obrasFiltradas() {
      if (!this.filtroObras) return this.obrasDisponibles
      return this.obrasDisponibles.filter(obra =>
        obra.direccion.toLowerCase().includes(this.filtroObras.toLowerCase()) ||
        (obra.descripcion && obra.descripcion.toLowerCase().includes(this.filtroObras.toLowerCase())) ||
        (obra.municipio && obra.municipio.toLowerCase().includes(this.filtroObras.toLowerCase())) ||
        (obra.provincia && obra.provincia.toLowerCase().includes(this.filtroObras.toLowerCase()))
      )
    }
  },
  mounted() {
    this.cargarDatosIniciales()
  },
  methods: {
    async cargarDatosIniciales() {
      try {
        console.log('🔄 Cargando datos iniciales para nuevo proyecto...')
        
        // Cargar todos los datos necesarios para proyectos y actividades
        const [
          estadosProyectos,
          empresas,
          tiposEmpresas,
          usuarios,
          tiposActividad,
          estadosActividad,
          accionesActividad
        ] = await Promise.all([
          EstadoProyectoService.getAll(),
          EmpresaService.getAll(),
          TipoEmpresaService.getAll(),
          usuarioService.getAllUsuarios(),
          TipoActividadService.getAll(),
          EstadoActividadService.getAll(),
          AccionActividadService.getAll()
        ])

        // Configurar estados de proyectos
        this.estadosProyectos = estadosProyectos
        this.opcionesEstado = [
          { value: null, text: 'Seleccione un estado' },
          ...estadosProyectos.map(estado => ({
            value: estado.idEstadoProyecto,
            text: estado.nombre
          }))
        ]
        
        console.log('Estados de proyectos cargados:', estadosProyectos)
        console.log('Opciones de estado configuradas:', this.opcionesEstado)
        
        // No establecer ningún estado por defecto - el usuario debe seleccionar uno
        console.log('Estados de proyectos cargados, sin selección por defecto')

        // Configurar empresas
        this.empresas = empresas

        // Configurar tipos de empresas
        this.tiposEmpresas = tiposEmpresas

        // Configurar usuarios para responsables
        this.usuarios = usuarios
        this.opcionesResponsables = [
          { value: '', text: 'Seleccionar responsable' },
          ...usuarios.map(usuario => ({
            value: usuario.idUsuario,
            text: `${usuario.nombre} ${usuario.primerApellido} ${usuario.segundoApellido || ''}`.trim()
          }))
        ]

        // Obtener usuario autenticado y establecer como responsable por defecto
        const usuarioAutenticado = authService.getCurrentUser()
        if (usuarioAutenticado) {
          // El usuario puede tener 'id' o 'idUsuario' dependiendo de la fuente
          const idUsuarioAutenticado = usuarioAutenticado.idUsuario || usuarioAutenticado.id
          
          if (idUsuarioAutenticado) {
            this.usuarioActual = {
              idUsuario: idUsuarioAutenticado
            }
            // Verificar si el usuario autenticado está en la lista de usuarios
            const usuarioEncontrado = usuarios.find(u => u.idUsuario === idUsuarioAutenticado)
            if (usuarioEncontrado) {
              // Establecer el responsable por defecto con el usuario autenticado
              this.proyecto.responsable = idUsuarioAutenticado
              console.log('✅ Responsable establecido por defecto:', idUsuarioAutenticado)
            } else {
              // Si no está en la lista, intentar cargar desde el servidor
              try {
                await authService.loadCurrentUser()
                const usuarioRefrescado = authService.getCurrentUser()
                if (usuarioRefrescado) {
                  const idUsuarioRefrescado = usuarioRefrescado.idUsuario || usuarioRefrescado.id
                  if (idUsuarioRefrescado) {
                    const usuarioActualizado = usuarios.find(u => u.idUsuario === idUsuarioRefrescado)
                    if (usuarioActualizado) {
                      this.usuarioActual = {
                        idUsuario: idUsuarioRefrescado
                      }
                      this.proyecto.responsable = idUsuarioRefrescado
                      console.log('✅ Responsable establecido por defecto (desde servidor):', idUsuarioRefrescado)
                    }
                  }
                }
              } catch (error) {
                console.warn('No se pudo cargar el usuario desde el servidor:', error)
              }
            }
          }
        }

        // Configurar opciones de actividades
        this.opcionesTipoActividad = [
          { value: null, text: 'Seleccionar tipo de actividad' },
          ...tiposActividad.map(tipo => ({
            value: tipo.idTipoActividad,
            text: tipo.nombre
          }))
        ]

        this.opcionesEstadoActividad = [
          { value: null, text: 'Seleccionar estado' },
          ...estadosActividad.map(estado => ({
            value: estado.idEstadoActividad,
            text: estado.nombre
          }))
        ]

        this.opcionesAccionActividad = [
          { value: null, text: 'Seleccionar acción' },
          ...accionesActividad.map(accion => ({
            value: accion.idAccionActividad,
            text: accion.nombre
          }))
        ]

        console.log('✅ Opciones de actividades cargadas:')
        console.log('- Tipos:', this.opcionesTipoActividad.length, 'opciones')
        console.log('- Estados:', this.opcionesEstadoActividad.length, 'opciones')
        console.log('- Acciones:', this.opcionesAccionActividad.length, 'opciones')

        // Los datos de obras se cargarán solo cuando se abra el modal de obra
        // para evitar errores 404 en servicios que no existen

        console.log('Datos iniciales cargados correctamente')
      } catch (error) {
        console.error('Error al cargar datos iniciales:', error)
        this.$toast?.error('Error al cargar los datos iniciales')
      }
    },
    async guardarProyecto() {
      // Validaciones básicas
      if (!this.proyecto.nombre.trim()) {
        this.$toast.warning('El nombre del proyecto es requerido')
        return
      }

      if (!this.proyecto.fechaInicio) {
        this.$toast.warning('La fecha de inicio es requerida')
        return
      }

      if (!this.proyecto.estado) {
        this.$toast.warning('El estado del proyecto es requerido')
        return
      }

      // Validar que el estado sea un número válido
      if (isNaN(parseInt(this.proyecto.estado))) {
        this.$toast.warning('El estado del proyecto debe ser un valor válido')
        return
      }

      // Validar que el estado no sea 0 (que podría ser considerado como "no seleccionado")
      if (parseInt(this.proyecto.estado) === 0) {
        this.$toast.warning('Debe seleccionar un estado válido para el proyecto')
        return
      }

      this.guardando = true
      try {
        // Preparar los datos del proyecto para enviar al backend
        // Usar la estructura correcta que el backend espera
        const proyectoData = {
          nombre: this.proyecto.nombre,
          descripcion: this.proyecto.descripcion || null,
          fechaInicio: this.proyecto.fechaInicio,
          fechaFin: this.proyecto.fechaFin || null,
          presupuesto: this.proyecto.presupuesto ? parseFloat(this.proyecto.presupuesto) : null,
          // El backend espera idEstadoProyecto, no estado
          idEstadoProyecto: parseInt(this.proyecto.estado),
          // El backend probablemente espera idResponsable
          idResponsable: this.proyecto.responsable ? parseInt(this.proyecto.responsable) : null,
          // Enviar solo los IDs de las obras, no los objetos completos
          obras: this.proyecto.obras ? this.proyecto.obras.map(obra => obra.idObra).filter(id => id != null) : []
          // NO enviar actividades aquí, se crearán después
        }

        // Verificar que el estado no sea null o undefined
        console.log('Verificación del estado:')
        console.log('- this.proyecto.estado:', this.proyecto.estado)
        console.log('- parseInt(this.proyecto.estado):', parseInt(this.proyecto.estado))
        console.log('- isNaN(parseInt(this.proyecto.estado)):', isNaN(parseInt(this.proyecto.estado)))
        console.log('- this.proyecto.estado === null:', this.proyecto.estado === null)
        console.log('- this.proyecto.estado === undefined:', this.proyecto.estado === undefined)
        console.log('- this.proyecto.estado === "":', this.proyecto.estado === "")
        console.log('- this.proyecto.estado === 0:', this.proyecto.estado === 0)

        console.log('Enviando proyecto al backend:', proyectoData)
        console.log('Estado del proyecto:', this.proyecto.estado)
        console.log('Tipo de estado:', typeof this.proyecto.estado)
        console.log('Estado parseado:', parseInt(this.proyecto.estado))
        console.log('¿Es NaN el estado?', isNaN(parseInt(this.proyecto.estado)))
        console.log('JSON completo:', JSON.stringify(proyectoData, null, 2))
        
        // Crear el proyecto en el backend
        // Usar la estructura correcta que el backend espera
        console.log('Enviando proyecto al backend:', proyectoData)
        console.log('JSON completo:', JSON.stringify(proyectoData, null, 2))
        
        const proyectoCreado = await ProyectoService.create(proyectoData)
        
        console.log('Proyecto creado exitosamente:', proyectoCreado)
        
        // Crear las actividades después de crear el proyecto
        if (this.proyecto.actividades && this.proyecto.actividades.length > 0) {
          console.log('Creando actividades para el proyecto:', this.proyecto.actividades.length)
          await this.crearActividadesParaProyecto(proyectoCreado.idProyecto, this.proyecto.actividades)
        }
        
        this.$toast.success('Proyecto creado correctamente')
        
        this.$router.push({ name: 'proyectos-lista' })
      } catch (error) {
        console.error('Error al crear proyecto:', error)
        
        // Manejo específico de errores
        let mensajeError = 'Error al crear el proyecto. Verifique los datos e intente nuevamente.'
        
        if (error.response && error.response.data) {
          const errorData = error.response.data
          if (errorData.message && errorData.message.includes('estado')) {
            mensajeError = 'Error: El estado del proyecto es obligatorio. Por favor, seleccione un estado válido.'
          } else if (errorData.message) {
            mensajeError = `Error: ${errorData.message}`
          }
        }
        
        this.$toast.error(mensajeError)
      } finally {
        this.guardando = false
      }
    },
    volver() {
      this.$router.push({ name: 'proyectos-lista' })
    },
    
    // Métodos para manejar empresas
    mostrarModalEmpresa() {
      this.resetModalEmpresa()
      this.$bvModal.show('modal-empresa')
    },
    
    resetModalEmpresa() {
      this.modalEmpresa = {
        nombre: '',
        direccion: '',
        idTipoEmpresa: '',
        cif: '',
        web: '',
        telefono: '',
        email: '',
        observaciones: '',
      }
    },
    
    async guardarEmpresa() {
      // Validaciones requeridas
      if (!this.modalEmpresa.nombre.trim()) {
        this.$toast.warning('El nombre de la empresa es requerido')
        return
      }
      
      if (!this.modalEmpresa.cif.trim()) {
        this.$toast.warning('El CIF es requerido')
        return
      }
      
      if (!this.modalEmpresa.direccion.trim()) {
        this.$toast.warning('La dirección es requerida')
        return
      }
      
      if (!this.modalEmpresa.idTipoEmpresa) {
        this.$toast.warning('El tipo de empresa es requerido')
        return
      }
      
      this.guardandoEmpresa = true
      try {
        // Crear la empresa
        const empresaCreada = await EmpresaService.create({
          nombre: this.modalEmpresa.nombre,
          direccion: this.modalEmpresa.direccion,
          idTipoEmpresa: this.modalEmpresa.idTipoEmpresa,
          cif: this.modalEmpresa.cif,
          web: this.modalEmpresa.web || null,
          telefono: this.modalEmpresa.telefono || null,
          email: this.modalEmpresa.email || null,
          observaciones: this.modalEmpresa.observaciones || null,
        })
        
        // Agregar la nueva empresa a la lista
        this.empresas.push(empresaCreada)
        
        // Seleccionar automáticamente la nueva empresa en el modal de obra
        this.modalObra.idEmpresa = empresaCreada.idEmpresa
        
        this.$toast.success('Empresa creada correctamente')
        
        this.$bvModal.hide('modal-empresa')
      } catch (error) {
        console.error('Error al crear empresa:', error)
        this.$toast.error('Error al crear la empresa')
      } finally {
        this.guardandoEmpresa = false
      }
    },
    
    // Métodos para manejar obras
    async mostrarModalObra() {
      console.log('Abriendo modal de obra...')
      console.log('Obras disponibles antes de cargar:', this.obrasDisponibles.length)
      this.resetModalObra()
      await this.cargarDatosObras()
      // Establecer la pestaña activa a "Crear Nueva Obra" (índice 1) después de cargar los datos
      this.modalObra.activeTab = 1
      console.log('Datos de obras cargados, mostrando modal')
      console.log('Obras disponibles después de cargar:', this.obrasDisponibles.length)
      console.log('Obras filtradas:', this.obrasFiltradas.length)
      this.$bvModal.show('modal-obra')
    },
    
    async cargarDatosObras() {
      try {
        console.log('Cargando obras para el modal...')
        
        // Cargar obras usando el mismo método que el listado
        this.obrasDisponibles = await ObraService.getAll()
        
        // Cargar catálogos necesarios para el formulario de obra
        await Promise.all([
          this.obtenerReferenciasObras(),
          this.obtenerFases(),
          this.obtenerEstadosObras(),
          this.obtenerTiposObras()
        ])
        
        console.log('Datos de obras cargados correctamente')
        console.log('Obras disponibles:', this.obrasDisponibles.length)
        console.log('Referencias:', this.referenciasObras.length)
        console.log('Fases:', this.fases.length)
        console.log('Estados:', this.estadosObras.length)
        console.log('Tipos:', this.tiposObras.length)
      } catch (error) {
        console.error('Error al cargar datos de obras:', error)
        this.$toast.error('Error al cargar las obras')
        // Mostrar datos vacíos en caso de error
        this.referenciasObras = []
        this.fases = []
        this.tiposObras = []
        this.estadosObras = []
        this.obrasDisponibles = []
      }
    },
    
    async obtenerReferenciasObras() {
      try {
        const referencias = await ReferenciaObraService.getAll()
        this.referenciasObras = referencias.sort((a, b) => a.nombre.localeCompare(b.nombre))
        return this.referenciasObras
      } catch (error) {
        console.error('Error cargando referencias de obras:', error)
        return []
      }
    },

    async obtenerFases() {
      try {
        const fases = await FaseService.getAll()
        this.fases = fases.sort((a, b) => a.nombre.localeCompare(b.nombre))
        return this.fases
      } catch (error) {
        console.error('Error cargando fases:', error)
        return []
      }
    },

    async obtenerEstadosObras() {
      try {
        const estados = await EstadoObraService.getAll()
        this.estadosObras = estados.sort((a, b) => a.nombre.localeCompare(b.nombre))
        return this.estadosObras
      } catch (error) {
        console.error('Error cargando estados de obras:', error)
        return []
      }
    },

    async obtenerTiposObras() {
      try {
        const tipos = await TipoObraService.getAll()
        this.tiposObras = tipos.sort((a, b) => a.nombre.localeCompare(b.nombre))
        return this.tiposObras
      } catch (error) {
        console.error('Error cargando tipos de obras:', error)
        return []
      }
    },
    
    resetModalObra() {
      this.modalObra = {
        editing: false,
        index: -1,
        activeTab: 0,
        // Información básica
        direccion: '',
        referenciaExterna: '',
        idReferenciaObra: '',
        idFase: '',
        idTipoObra: '',
        idEstadoObra: '',
        // Ubicación
        municipio: '',
        provincia: '',
        codigoPostal: '',
        idEmpresa: '',
        // Fechas
        fechaInicioObra: '',
        fechaFinObra: '',
        // Información adicional
        magnitudes: '',
        descripcion: '',
      }
      // Limpiar errores de validación
      this.erroresObra = {
        direccion: null,
        idFase: null,
        idTipoObra: null,
        idEstadoObra: null,
        municipio: null,
        provincia: null,
        codigoPostal: null,
        idEmpresa: null,
        fechaInicioObra: null,
      }
      this.filtroObras = ''
      this.obraSeleccionada = null
    },
    
    editarObra(obra, index) {
      // Limpiar errores de validación
      this.erroresObra = {
        direccion: null,
        idFase: null,
        idTipoObra: null,
        idEstadoObra: null,
        municipio: null,
        provincia: null,
        codigoPostal: null,
        idEmpresa: null,
        fechaInicioObra: null,
      }
      this.modalObra = {
        editing: true,
        index: index,
        activeTab: 1, // Ir directamente a la pestaña de crear/editar
        // Información básica
        direccion: obra.direccion || '',
        referenciaExterna: obra.referenciaExterna || '',
        idReferenciaObra: obra.idReferenciaObra || '',
        idFase: obra.idFase || '',
        idTipoObra: obra.idTipoObra || '',
        idEstadoObra: obra.idEstadoObra || '',
        // Ubicación
        municipio: obra.municipio || '',
        provincia: obra.provincia || '',
        codigoPostal: obra.codigoPostal || '',
        idEmpresa: obra.idEmpresa || '',
        // Fechas
        fechaInicioObra: obra.fechaInicioObra || '',
        fechaFinObra: obra.fechaFinObra || '',
        // Información adicional
        magnitudes: obra.magnitudes || '',
        descripcion: obra.descripcion || '',
      }
      this.$bvModal.show('modal-obra')
    },
    
    // Métodos para selección de obras
    seleccionarObra(obra) {
      this.obraSeleccionada = obra
    },
    
    agregarObraSeleccionada() {
      if (!this.obraSeleccionada) return
      
      // Verificar si la obra ya está agregada al proyecto
      const yaExiste = this.proyecto.obras.some(obra => obra.idObra === this.obraSeleccionada.idObra)
      if (yaExiste) {
        this.$toast.warning('Esta obra ya está agregada al proyecto')
        return
      }
      
      // Agregar la obra seleccionada al proyecto
      this.proyecto.obras.push({
        ...this.obraSeleccionada,
        estado: 'Pendiente', // Resetear estado al agregar al proyecto
      })
      
      this.$toast.success('Obra agregada al proyecto correctamente')
      
      this.$bvModal.hide('modal-obra')
    },
    
    getObraBadgeVariant(estado) {
      const variantes = {
        'Pendiente': 'secondary',
        'En Progreso': 'info',
        'Completada': 'success',
        'Cancelada': 'danger',
      }
      return variantes[estado] || 'secondary'
    },
    
    async guardarObra() {
      // Limpiar errores previos
      this.erroresObra = {
        direccion: null,
        idFase: null,
        idTipoObra: null,
        idEstadoObra: null,
        municipio: null,
        provincia: null,
        codigoPostal: null,
        idEmpresa: null,
        fechaInicioObra: null,
      }
      
      // Validaciones requeridas y marcar campos con errores
      let hayErrores = false
      
      if (!this.modalObra.direccion.trim()) {
        this.erroresObra.direccion = false
        hayErrores = true
      }
      
      if (!this.modalObra.idFase) {
        this.erroresObra.idFase = false
        hayErrores = true
      }
      
      if (!this.modalObra.idTipoObra) {
        this.erroresObra.idTipoObra = false
        hayErrores = true
      }
      
      if (!this.modalObra.idEstadoObra) {
        this.erroresObra.idEstadoObra = false
        hayErrores = true
      }
      
      if (!this.modalObra.municipio.trim()) {
        this.erroresObra.municipio = false
        hayErrores = true
      }
      
      if (!this.modalObra.provincia.trim()) {
        this.erroresObra.provincia = false
        hayErrores = true
      }
      
      if (!this.modalObra.codigoPostal) {
        this.erroresObra.codigoPostal = false
        hayErrores = true
      }
      
      if (!this.modalObra.idEmpresa) {
        this.erroresObra.idEmpresa = false
        hayErrores = true
      }
      
      if (!this.modalObra.fechaInicioObra) {
        this.erroresObra.fechaInicioObra = false
        hayErrores = true
      }
      
      if (hayErrores) {
        this.$toast.error('Por favor, complete todos los campos requeridos')
        // Hacer scroll al primer campo con error
        this.$nextTick(() => {
          const primerError = document.querySelector('.is-invalid')
          if (primerError) {
            primerError.scrollIntoView({ behavior: 'smooth', block: 'center' })
            primerError.focus()
          }
        })
        return
      }
      
      try {
        // Crear el objeto obra para enviar al backend
        const obraData = {
          // Información básica
          direccion: this.modalObra.direccion,
          referenciaExterna: this.modalObra.referenciaExterna || null,
          idReferenciaObra: this.modalObra.idReferenciaObra || null,
          idFase: this.modalObra.idFase,
          idTipoObra: this.modalObra.idTipoObra,
          idEstadoObra: this.modalObra.idEstadoObra,
          // Ubicación
          municipio: this.modalObra.municipio,
          provincia: this.modalObra.provincia,
          codigoPostal: parseInt(this.modalObra.codigoPostal),
          idEmpresa: this.modalObra.idEmpresa,
          // Fechas
          fechaInicioObra: this.modalObra.fechaInicioObra,
          fechaFinObra: this.modalObra.fechaFinObra || null,
          // Información adicional
          magnitudes: this.modalObra.magnitudes || null,
          descripcion: this.modalObra.descripcion || null,
        }
        
        // Guardar la obra en el backend
        const obraCreada = await ObraService.create(obraData)
        
        // Crear el objeto obra para mostrar en el frontend
        const obra = {
          ...obraCreada,
          // Campos adicionales para compatibilidad con la vista
          nombre: obraCreada.direccion, // Usar dirección como nombre
          estado: this.estadosObras.find(e => e.idEstadoObra === obraCreada.idEstadoObra)?.nombre || 'Pendiente',
        }
        
        if (this.modalObra.editing) {
          this.proyecto.obras.splice(this.modalObra.index, 1, obra)
          this.$toast.success('Obra actualizada correctamente')
        } else {
          this.proyecto.obras.push(obra)
          this.$toast.success('Obra creada y guardada correctamente')
        }
        
        this.$bvModal.hide('modal-obra')
      } catch (error) {
        console.error('Error al guardar obra:', error)
        this.$toast.error('Error al guardar la obra')
      }
    },
    
    eliminarObra(index) {
      this.$bvModal.msgBoxConfirm('¿Está seguro de que desea eliminar esta obra?', {
        title: 'Confirmar eliminación',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Eliminar',
        cancelTitle: 'Cancelar',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true
      }).then(value => {
        if (value) {
          this.proyecto.obras.splice(index, 1)
          this.$toast.success('Obra eliminada correctamente')
        }
      })
    },
    
    // Métodos para manejar actividades
    mostrarModalActividad() {
      this.resetModalActividad()
      this.$bvModal.show('modal-actividad')
    },
    
    resetModalActividad() {
      this.modalActividad = {
        tipoActividad: null,
        fecha: '',
        estadoActividad: null,
        observaciones: '',
        proximaAccionFecha: '',
        proximaAccionTipo: null,
        recordatorio: false,
      }
      // Limpiar variables de subida de documentos
      this.mostrarSubidaDocumentos = false
      this.actividadTemporalId = null
      this.guardandoActividad = false
      this.documentosActividad = []
      // Limpiar variables de edición
      this.editandoActividad = false
      this.indiceActividadEditando = null
    },
    
    async guardarActividadConDocumentos() {
      if (!this.modalActividad.tipoActividad || !this.modalActividad.fecha || !this.modalActividad.estadoActividad) {
        this.$toast.warning('Los campos obligatorios son requeridos')
        return
      }
      
      this.guardandoActividad = true
      
      try {
        // Preparar datos de la actividad SIN documentos
        const actividadData = {
          idTipoActividad: this.modalActividad.tipoActividad,
          idEstadoActividad: this.modalActividad.estadoActividad,
          fecha: this.modalActividad.fecha,
          proximaAccionFecha: this.modalActividad.proximaAccionFecha,
          idProximaAccionTipo: this.modalActividad.proximaAccionTipo,
          observaciones: this.modalActividad.observaciones || '',
          recordatorio: this.modalActividad.recordatorio,
          idCreadoPor: this.usuarioActual.idUsuario,
          idProyecto: this.proyecto.idProyecto // Asociar al proyecto
        }

        console.log('📤 Enviando actividad al backend:', actividadData)
        
        let actividadCreada = null
        if (this.editandoActividad) {
          // Actualizar actividad existente
          const actividadExistente = this.proyecto.actividades[this.indiceActividadEditando]
          if (actividadExistente && actividadExistente.idActividad) {
            const response = await this.$http.put(`/actividades/${actividadExistente.idActividad}`, actividadData)
            actividadCreada = response.data
          }
        } else {
          // Crear nueva actividad en el backend
          const response = await this.$http.post('/actividades', actividadData)
          actividadCreada = response.data
          
          console.log('✅ Actividad creada en backend:', actividadCreada)
          console.log('✅ ID de actividad:', actividadCreada?.idActividad)
          
          if (!actividadCreada || !actividadCreada.idActividad) {
            throw new Error('No se pudo obtener el ID de la actividad creada')
          }
          
          // Si la actividad tiene documentos, crearlos
          if (this.documentosActividad && this.documentosActividad.length > 0) {
            console.log('📎 Creando documentos para actividad:', this.documentosActividad.length)
            await this.crearDocumentosParaActividad(actividadCreada.idActividad, this.documentosActividad)
          }
        }
        
        // Preparar datos de la actividad para la vista
        const actividadTemporal = {
          id: actividadCreada ? actividadCreada.idActividad : (this.editandoActividad ? this.proyecto.actividades[this.indiceActividadEditando].id : Date.now()),
          idActividad: actividadCreada ? actividadCreada.idActividad : (this.editandoActividad && this.proyecto.actividades[this.indiceActividadEditando].idActividad ? this.proyecto.actividades[this.indiceActividadEditando].idActividad : null),
          tipoActividad: this.modalActividad.tipoActividad,
          fecha: this.modalActividad.fecha,
          estadoActividad: this.modalActividad.estadoActividad,
          observaciones: this.modalActividad.observaciones,
          proximaAccionFecha: this.modalActividad.proximaAccionFecha,
          proximaAccionTipo: this.modalActividad.proximaAccionTipo,
          recordatorio: this.modalActividad.recordatorio,
          usuario: 'Usuario Actual',
          timestamp: new Date().toISOString(),
          documentos: this.documentosActividad
        }
        
        if (this.editandoActividad) {
          // Actualizar actividad existente
          this.proyecto.actividades[this.indiceActividadEditando] = actividadTemporal
          this.$toast.success('Actividad actualizada exitosamente')
        } else {
          // Agregar nueva actividad
          this.proyecto.actividades.unshift(actividadTemporal)
          this.$toast.success('Actividad creada exitosamente')
        }
        
        // Guardar el estado del recordatorio antes de cerrar el modal
        const recordatorioMarcado = this.modalActividad.recordatorio
        
        // Cerrar modal y limpiar
        this.$bvModal.hide('modal-actividad')
        this.resetModalActividad()
        
        // Si el recordatorio está marcado, mostrar las opciones de recordatorio después de cerrar el modal
        if (recordatorioMarcado) {
          console.log('📅 Enviando recordatorio para actividad:', actividadTemporal)
          console.log('📅 ID de actividad para recordatorio:', actividadTemporal.idActividad)
          await this.agregarRecordatorioDesdeModal(actividadTemporal)
        }
        
      } catch (error) {
        console.error('Error al guardar actividad:', error)
        this.$toast({
          message: 'Error al crear la actividad',
          variant: 'danger',
        })
      } finally {
        this.guardandoActividad = false
      }
    },

    async crearActividad(actividadData) {
      try {
        const response = await this.$http.post('/actividades', actividadData)
        return response.data
      } catch (error) {
        console.error('Error al crear actividad:', error)
        throw error
      }
    },
    
    async crearActividadesParaProyecto(proyectoId, actividades) {
      try {
        for (const actividad of actividades) {
          // Validar que los campos obligatorios estén presentes
          if (!actividad.tipoActividad) {
            console.error('❌ Actividad sin tipo:', actividad)
            continue
          }
          if (!actividad.estadoActividad) {
            console.error('❌ Actividad sin estado:', actividad)
            continue
          }
          if (!actividad.fecha) {
            console.error('❌ Actividad sin fecha:', actividad)
            continue
          }
          
          // Mapear los campos de la actividad temporal a la estructura que espera el backend
          const actividadData = {
            idTipoActividad: actividad.tipoActividad,
            idEstadoActividad: actividad.estadoActividad,
            fecha: actividad.fecha,
            proximaAccionFecha: actividad.proximaAccionFecha || null,
            idProximaAccionTipo: actividad.proximaAccionTipo || null,
            observaciones: actividad.observaciones || '',
            recordatorio: actividad.recordatorio || false,
            idCreadoPor: this.usuarioActual.idUsuario,
            idProyecto: proyectoId
          }
          
          console.log('📤 Creando actividad para proyecto:', actividadData)
          console.log('📤 JSON completo:', JSON.stringify(actividadData, null, 2))
          const actividadCreada = await this.$http.post('/actividades', actividadData)
          
          // Si la actividad tiene documentos, crearlos
          if (actividad.documentos && actividad.documentos.length > 0) {
            console.log('📎 Creando documentos para actividad:', actividad.documentos.length)
            await this.crearDocumentosParaActividad(actividadCreada.data.idActividad, actividad.documentos)
          }
        }
        console.log('✅ Actividades creadas para proyecto:', proyectoId)
      } catch (error) {
        console.error('Error al crear actividades:', error)
        throw error
      }
    },
    
    async crearDocumentosParaActividad(actividadId, documentos) {
      try {
        for (const documento of documentos) {
          // Mapear los campos del documento temporal a la estructura que espera el backend
          const documentoData = {
            nombreArchivo: documento.nombreArchivo,
            nombreArchivoOriginal: documento.nombreArchivoOriginal,
            idCategoriaArchivo: documento.idCategoriaArchivo,
            version: documento.version,
            contenido: documento.contenido,
            idActividad: actividadId,
            idSubidoPor: this.usuarioActual.idUsuario
          }
          
          console.log('📎 Creando documento:', documentoData.nombreArchivo)
          console.log('📎 Datos del documento:', JSON.stringify(documentoData, null, 2))
          const documentoCreado = await this.$http.post('/documentos', documentoData)
          console.log('✅ Documento creado exitosamente:', documentoCreado.data)
        }
        console.log('✅ Documentos creados para actividad:', actividadId)
      } catch (error) {
        console.error('Error al crear documentos:', error)
        throw error
      }
    },
    
    // Métodos para manejar la subida de documentos en actividades
    onDocumentoSubido(documento) {
      // Agregar documento a la lista temporal
      this.documentosActividad.push({
        nombreArchivo: documento.nombreArchivo,
        nombreArchivoOriginal: documento.nombreArchivoOriginal,
        idCategoriaArchivo: documento.idCategoriaArchivo,
        nombreCategoria: documento.nombreCategoria,
        version: documento.version,
        contenido: documento.contenido,
        tamaño: documento.tamaño || 0
      })
      
      this.$toast({
        message: 'Documento agregado correctamente',
        variant: 'success',
      })
      
      // Cerrar el modal de subida
      this.mostrarSubidaDocumentos = false
    },

    onFinalizarSubidaDocumentos() {
      this.mostrarSubidaDocumentos = false
    },

    descargarDocumento(index) {
      try {
        const documento = this.documentosActividad[index]
        if (!documento || !documento.contenido) {
          this.$toast.error('El documento no tiene contenido disponible')
          return
        }

        // Usar el nombre original si está disponible, sino el nombre del archivo
        const nombreDescarga = documento.nombreArchivoOriginal || documento.nombreArchivo
        
        // Determinar el tipo MIME basado en la extensión del archivo
        const extension = nombreDescarga.split('.').pop()?.toLowerCase()
        const mimeTypes = {
          'pdf': 'application/pdf',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'xls': 'application/vnd.ms-excel',
          'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'ppt': 'application/vnd.ms-powerpoint',
          'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'txt': 'text/plain',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'zip': 'application/zip',
          'rar': 'application/x-rar-compressed'
        }
        
        const mimeType = extension ? mimeTypes[extension] || 'application/octet-stream' : 'application/octet-stream'
        
        // Descargar el archivo usando el método del servicio
        DocumentoService.downloadFileFromBase64(documento.contenido, nombreDescarga, mimeType)
        
        this.$toast.success('Documento descargado correctamente')
      } catch (error) {
        console.error('Error al descargar documento:', error)
        this.$toast.error('Error al descargar el documento')
      }
    },

    eliminarDocumento(index) {
      this.documentosActividad.splice(index, 1)
      this.$toast({
        message: 'Documento eliminado',
        variant: 'info',
      })
    },

    resetSubidaDocumentos() {
      this.mostrarSubidaDocumentos = false
    },
    
    // Métodos para editar actividades temporales
    editarActividad(index) {
      const actividad = this.proyecto.actividades[index]
      this.editandoActividad = true
      this.indiceActividadEditando = index
      
      // Cargar datos de la actividad en el modal
      this.modalActividad = {
        tipoActividad: actividad.tipoActividad,
        fecha: actividad.fecha,
        estadoActividad: actividad.estadoActividad,
        observaciones: actividad.observaciones,
        proximaAccionFecha: actividad.proximaAccionFecha,
        proximaAccionTipo: actividad.proximaAccionTipo,
        recordatorio: actividad.recordatorio
      }
      
      // Cargar documentos de la actividad
      this.documentosActividad = actividad.documentos || []
      
      // Abrir modal
      this.$bvModal.show('modal-actividad')
    },
    
    eliminarActividad(index) {
      this.$swal({
        title: '¿Estás seguro?',
        text: 'Esta actividad será eliminada permanentemente',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6'
      }).then((result) => {
        if (result.isConfirmed) {
          this.proyecto.actividades.splice(index, 1)
          this.$toast({
            message: 'Actividad eliminada',
            variant: 'success',
          })
        }
      })
    },

    // Métodos auxiliares para actividades
    getActividadIcon(tipo) {
      const iconos = {
        'Inicio': 'PlayIcon',
        'Progreso': 'TrendingUpIcon',
        'Reunión': 'UsersIcon',
        'Revisión': 'EyeIcon',
        'Finalización': 'CheckCircleIcon',
        'Problema': 'AlertTriangleIcon',
        'Otro': 'ActivityIcon',
      }
      return iconos[tipo] || 'ActivityIcon'
    },
    
    getActividadBadgeVariant(tipo) {
      const variantes = {
        'Inicio': 'primary',
        'Progreso': 'info',
        'Reunión': 'warning',
        'Revisión': 'secondary',
        'Finalización': 'success',
        'Problema': 'danger',
        'Otro': 'light',
      }
      return variantes[tipo] || 'light'
    },
    
    formatearFecha(fecha) {
      if (!fecha) return ''
      const fechaObj = new Date(fecha)
      return fechaObj.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    // Métodos para importar desde Construdata
    mostrarModalImportarConstrudata() {
      this.$refs.modalImportarConstrudata.show()
    },
    limpiarFormularioConstrudata() {
      this.textoConstrudata = ''
      this.datosParseados = null
      this.procesandoConstrudata = false
    },
    async importarDatosConstrudata() {
      if (!this.textoConstrudata.trim()) {
        this.$toast.error('Por favor, pega el texto de Construdata')
        return
      }

      this.procesandoConstrudata = true
      
      try {
        // Parsear el texto de Construdata
        const datos = this.parsearTextoConstrudata(this.textoConstrudata)
        this.datosParseados = datos
        
        // Mapear los datos a los campos del formulario
        this.mapearDatosAFormulario(datos)
        
        this.$toast.success('Datos importados correctamente')
        this.$refs.modalImportarConstrudata.hide()
        this.limpiarFormularioConstrudata()
      } catch (error) {
        this.$toast.error('Error al procesar los datos de Construdata')
        console.error('Error:', error)
      } finally {
        this.procesandoConstrudata = false
      }
    },
    parsearTextoConstrudata(texto) {
      const lineas = texto.split('\n').map(linea => linea.trim()).filter(linea => linea)
      const datos = {}
      
      // Buscar nombre y referencia en la primera línea
      const primeraLinea = lineas[0] || ''
      const matchRef = primeraLinea.match(/Ref\.:\s*([^\s]+)/)
      if (matchRef) {
        datos.referencia = matchRef[1]
        datos.nombre = primeraLinea.replace(/\s*Ref\.:\s*[^\s]+.*/, '').trim()
      } else {
        datos.nombre = primeraLinea
      }
      
      // Buscar dirección
      const indiceDireccion = lineas.findIndex(linea => 
        linea.toUpperCase().includes('DIRECCIÓN') || 
        linea.toUpperCase().includes('DIRECCION')
      )
      if (indiceDireccion !== -1 && indiceDireccion + 1 < lineas.length) {
        const direccionCompleta = lineas[indiceDireccion + 1]
        datos.direccion = direccionCompleta
        
        // Extraer municipio, provincia y código postal
        const matchDireccion = direccionCompleta.match(/^([^(]+)\s*\((\d{5})\)\s*(.+),\s*(.+)$/)
        if (matchDireccion) {
          datos.municipio = matchDireccion[1].trim()
          datos.codigoPostal = matchDireccion[2]
          datos.provincia = matchDireccion[3].trim()
        }
      }
      
      // Buscar tipo de obra
      const indiceTipoObra = lineas.findIndex(linea => 
        linea.toUpperCase().includes('TIPO DE OBRA')
      )
      if (indiceTipoObra !== -1 && indiceTipoObra + 1 < lineas.length) {
        datos.tipoObra = lineas[indiceTipoObra + 1]
      }
      
      // Buscar promotor
      const indicePromotor = lineas.findIndex(linea => 
        linea.toUpperCase().includes('PROMOTOR')
      )
      if (indicePromotor !== -1 && indicePromotor + 1 < lineas.length) {
        datos.promotor = lineas[indicePromotor + 1]
      }
      
      // Buscar descripción
      const indiceDescripcion = lineas.findIndex(linea => 
        linea.toUpperCase().includes('DESCRIPCIÓN') || 
        linea.toUpperCase().includes('DESCRIPCION')
      )
      if (indiceDescripcion !== -1 && indiceDescripcion + 1 < lineas.length) {
        // La descripción puede ocupar varias líneas
        let descripcion = ''
        for (let i = indiceDescripcion + 1; i < lineas.length; i++) {
          if (lineas[i].toUpperCase().includes('PROMOTORES')) {
            break
          }
          if (descripcion) descripcion += ' '
          descripcion += lineas[i]
        }
        datos.descripcion = descripcion.trim()
      }
      
      return datos
    },
    mapearDatosAFormulario(datos) {
      // Mapear datos básicos
      if (datos.referencia) {
        this.modalObra.referenciaExterna = datos.referencia
      }
      
      if (datos.direccion) {
        this.modalObra.direccion = datos.direccion
      }
      
      if (datos.municipio) {
        this.modalObra.municipio = datos.municipio
      }
      
      if (datos.provincia) {
        this.modalObra.provincia = datos.provincia
      }
      
      if (datos.codigoPostal) {
        this.modalObra.codigoPostal = datos.codigoPostal
      }
      
      if (datos.descripcion) {
        this.modalObra.descripcion = datos.descripcion
      }
    },

    // Métodos para manejar recordatorios
    async agregarRecordatorioDesdeModal(actividad) {
      // Validar que haya fecha de próxima acción
      if (!actividad.proximaAccionFecha) {
        this.$swal({
          title: 'Fecha requerida',
          text: 'Debe seleccionar una fecha de próxima acción para configurar el recordatorio',
          icon: 'warning',
          confirmButtonText: 'Aceptar',
          customClass: {
            confirmButton: 'btn btn-primary',
          },
          buttonsStyling: false,
        })
        return
      }
      
      // Usar solo la fecha de próxima acción
      const fechaRecordatorio = actividad.proximaAccionFecha
      const result = await this.$swal({
        title: 'Configurar Recordatorio',
        html: `
          <div class="text-left">
            <p>Selecciona la hora para el recordatorio:</p>
            <div class="form-group">
              <label for="hora">Hora:</label>
              <input type="time" id="hora" class="form-control" value="09:00" required>
            </div>
            <p class="text-muted small mt-2">Fecha: ${this.formatearFecha(fechaRecordatorio)}</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
        didOpen: () => {
          // Asegurar que el input esté enfocado
          setTimeout(() => {
            const input = document.getElementById('hora')
            if (input) input.focus()
          }, 100)
        },
        preConfirm: () => {
          const horaInput = document.getElementById('hora')
          if (!horaInput || !horaInput.value) {
            this.$swal.showValidationMessage('Por favor selecciona una hora')
            return false
          }
          return horaInput.value
        }
      })

      if (!result.value) {
        // Si se cancela, simplemente retornar
        return
      }
      
      const horaRecordatorio = result.value

      // Construir la actividad completa para el evento
      const actividadCompleta = this.construirActividadParaRecordatorio(actividad)
      
      // Crear el evento con la hora seleccionada
      const evento = this.crearEventoDesdeActividadCompleto(actividadCompleta, horaRecordatorio)
      
      // Guardar referencia para usar en el segundo diálogo
      const self = this
      
      this.$swal({
        title: 'Agregar Recordatorio',
        text: 'Selecciona cómo quieres agregar el recordatorio:',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Google Calendar',
        cancelButtonText: 'Cancelar',
        showDenyButton: true,
        denyButtonText: 'Outlook',
        showCloseButton: true,
        customClass: {
          confirmButton: 'btn btn-success',
          denyButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          // Google Calendar
          const url = generarEnlaceGoogleCalendar(evento)
          window.open(url, '_blank')
        } else if (result.isDenied) {
          // Outlook
          const url = generarEnlaceOutlookCalendar(evento)
          window.open(url, '_blank')
        }
      })
      
      // Mostrar opciones adicionales
      this.$swal({
        title: 'Otras opciones',
        text: 'También puedes descargar el archivo o enviarlo por correo:',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Descargar archivo',
        cancelButtonText: 'Cerrar',
        showDenyButton: true,
        denyButtonText: 'Enviar por correo',
        customClass: {
          confirmButton: 'btn btn-info',
          denyButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          descargarArchivoICS(evento)
        } else if (result.isDenied) {
          await self.enviarRecordatorioPorCorreoDesdeModal(actividadCompleta, horaRecordatorio)
        }
      })
    },

    construirActividadParaRecordatorio(actividad) {
      // Obtener los nombres de los campos desde las opciones
      const tipoActividadNombre = this.opcionesTipoActividad.find(
        opcion => opcion.value === actividad.tipoActividad
      )?.text || 'Actividad'

      const estadoActividadNombre = this.opcionesEstadoActividad.find(
        opcion => opcion.value === actividad.estadoActividad
      )?.text || ''

      const proximaAccionNombre = this.opcionesAccionActividad.find(
        opcion => opcion.value === actividad.proximaAccionTipo
      )?.text || ''

      return {
        // Preservar el idActividad y id para poder enviar el recordatorio
        idActividad: actividad.idActividad,
        id: actividad.id,
        tipoActividad: {
          idTipoActividad: actividad.tipoActividad,
          nombre: tipoActividadNombre
        },
        estadoActividad: {
          idEstadoActividad: actividad.estadoActividad,
          nombre: estadoActividadNombre
        },
        proximaAccionTipo: {
          idAccionActividad: actividad.proximaAccionTipo,
          nombre: proximaAccionNombre
        },
        proximaAccionFecha: actividad.proximaAccionFecha,
        observaciones: actividad.observaciones,
        nombreProyecto: this.proyecto.nombre || ''
      }
    },

    crearEventoDesdeActividadCompleto(actividad, hora = '09:00') {
      // Usar solo la fecha de próxima acción para el recordatorio
      if (!actividad.proximaAccionFecha) {
        throw new Error('La fecha de próxima acción es requerida')
      }
      
      const fechaBase = new Date(actividad.proximaAccionFecha)
      
      // Combinar la fecha con la hora seleccionada
      const [horas, minutos] = hora.split(':').map(Number)
      const fechaEvento = new Date(fechaBase)
      fechaEvento.setHours(horas, minutos, 0, 0)
      
      // Crear fecha de fin (1 hora después)
      const fechaFin = new Date(fechaEvento)
      fechaFin.setHours(fechaEvento.getHours() + 1)
      
      // Construir el título con el tipo de actividad
      const titulo = actividad.tipoActividad?.nombre || 'Actividad'
      
      // Construir la descripción completa con toda la información
      let descripcion = ''
      
      if (actividad.estadoActividad?.nombre) {
        descripcion += `Estado: ${actividad.estadoActividad.nombre}\n`
      }
      
      if (actividad.proximaAccionTipo?.nombre) {
        descripcion += `Próxima acción: ${actividad.proximaAccionTipo.nombre}\n`
      }
      
      if (actividad.nombreProyecto) {
        descripcion += `Proyecto: ${actividad.nombreProyecto}\n`
      }
      
      if (actividad.observaciones) {
        descripcion += `\nObservaciones:\n${actividad.observaciones}`
      }
      
      if (!descripcion) {
        descripcion = 'Actividad del proyecto'
      }
      
      return {
        titulo: titulo,
        descripcion: descripcion.trim(),
        fechaInicio: fechaEvento,
        fechaFin: fechaFin,
        ubicacion: actividad.nombreProyecto || ''
      }
    },

    generarArchivoICS(evento) {
      const formatearFechaParaICS = (fecha) => {
        return fecha.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
      }
      
      const fechaInicio = formatearFechaParaICS(evento.fechaInicio)
      const fechaFin = formatearFechaParaICS(evento.fechaFin || evento.fechaInicio)
      const fechaActual = formatearFechaParaICS(new Date())
      
      const descripcion = evento.descripcion
        ? evento.descripcion.replace(/\n/g, '\\n').replace(/,/g, '\\,')
        : ''
      
      const ubicacion = evento.ubicacion ? evento.ubicacion.replace(/,/g, '\\,') : ''
      
      return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Gescom//Actividad//ES
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
DTSTART:${fechaInicio}
DTEND:${fechaFin}
DTSTAMP:${fechaActual}
SUMMARY:${evento.titulo || 'Actividad'}
DESCRIPTION:${descripcion}
LOCATION:${ubicacion}
STATUS:CONFIRMED
SEQUENCE:0
END:VEVENT
END:VCALENDAR`
    },

    async enviarRecordatorioPorCorreoDesdeModal(actividad, horaRecordatorio) {
      const { value: emails } = await this.$swal({
        title: 'Enviar Recordatorio por Correo',
        html: `
          <div class="text-left">
            <p>Ingresa las direcciones de correo electrónico (separadas por comas):</p>
            <div class="form-group">
              <label for="emails">Correos electrónicos:</label>
              <textarea id="emails" class="form-control" rows="3" placeholder="ejemplo1@correo.com, ejemplo2@correo.com" required></textarea>
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Enviar',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-warning',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
        didOpen: () => {
          setTimeout(() => {
            const textarea = document.getElementById('emails')
            if (textarea) textarea.focus()
          }, 100)
        },
        preConfirm: () => {
          const emailsInput = document.getElementById('emails')
          if (!emailsInput || !emailsInput.value.trim()) {
            this.$swal.showValidationMessage('Por favor ingresa al menos un correo electrónico')
            return false
          }
          // Validar formato de emails básico
          const emails = emailsInput.value.split(',').map(email => email.trim()).filter(email => email)
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          const emailsInvalidos = emails.filter(email => !emailRegex.test(email))
          if (emailsInvalidos.length > 0) {
            this.$swal.showValidationMessage(`Los siguientes correos no son válidos: ${emailsInvalidos.join(', ')}`)
            return false
          }
          return emails
        }
      })

      if (!emails) {
        return
      }

      try {
        this.$swal({
          title: 'Enviando...',
          text: `Enviando recordatorio a ${emails.length} destinatario${emails.length > 1 ? 's' : ''}...`,
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            this.$swal.showLoading()
          }
        })

        const evento = this.crearEventoDesdeActividadCompleto(actividad, horaRecordatorio)
        const archivoICS = this.generarArchivoICS(evento)
        
        // Obtener el idActividad de la actividad (puede estar en idActividad o id)
        const actividadId = actividad.idActividad || actividad.id || null
        
        console.log('📧 Enviando recordatorio - Actividad recibida:', actividad)
        console.log('📧 ID de actividad extraído:', actividadId)
        console.log('📧 idActividad:', actividad.idActividad)
        console.log('📧 id:', actividad.id)
        
        if (!actividadId) {
          console.error('❌ No se pudo obtener el ID de la actividad:', actividad)
          throw new Error('No se pudo obtener el ID de la actividad. Por favor, guarda la actividad primero.')
        }
        
        await this.enviarCorreoRecordatorio(actividadId, emails, archivoICS, evento)
        
        this.$swal({
          title: '¡Enviado!',
          text: `El recordatorio ha sido enviado a ${emails.length} destinatario${emails.length > 1 ? 's' : ''}`,
          icon: 'success',
          confirmButtonText: 'Aceptar',
          customClass: {
            confirmButton: 'btn btn-success',
          },
          buttonsStyling: false,
        })
      } catch (error) {
        console.error('Error al enviar el correo:', error)
        this.$swal({
          title: 'Error',
          text: 'No se pudo enviar el recordatorio. Inténtalo de nuevo.',
          icon: 'error',
          confirmButtonText: 'Aceptar',
          customClass: {
            confirmButton: 'btn btn-danger',
          },
          buttonsStyling: false,
        })
      }
    },

    async enviarCorreoRecordatorio(actividadId, emails, archivoICS, evento) {
      try {
        const response = await this.$http.post('/email/recordatorio-actividad', {
          actividadId: actividadId,
          emailsDestino: emails,
          archivoICS: archivoICS,
          evento: evento
        })
        
        if (response.data.success) {
          return Promise.resolve()
        } else {
          throw new Error(response.data.message || 'Error al enviar el correo')
        }
      } catch (error) {
        console.error('Error al enviar correo:', error)
        throw new Error('Error al enviar el recordatorio por correo')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.proyectos-nuevo {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.gap-2 {
  gap: 0.5rem;
}

/* Estilos para el timeline de actividades */
.timeline {
  position: relative;
  padding-left: 2rem;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 1rem;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e9ecef;
}

.timeline-item {
  position: relative;
  margin-bottom: 2rem;
  padding-left: 2rem;
}

.timeline-marker {
  position: absolute;
  left: -1.5rem;
  top: 0.5rem;
  width: 2rem;
  height: 2rem;
  background: #fff;
  border: 2px solid #007bff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.timeline-content {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 1rem;
  position: relative;
}

.timeline-content::before {
  content: '';
  position: absolute;
  left: -0.5rem;
  top: 1rem;
  width: 0;
  height: 0;
  border-top: 0.5rem solid transparent;
  border-bottom: 0.5rem solid transparent;
  border-right: 0.5rem solid #e9ecef;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.timeline-meta {
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
}

/* Estilos para la lista de obras */
.obras-list {
  max-height: 400px;
  overflow-y: auto;
}

.obra-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
}

.obra-item:hover {
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.1);
}

.obra-item.selected {
  border-color: #007bff;
  background: #f8f9ff;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.obra-content {
  flex: 1;
  margin-right: 1rem;
}

.obra-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.obra-descripcion {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

.obra-meta {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
}

.obra-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f8f9fa;
  transition: all 0.2s ease;
}

.obra-item.selected .obra-actions {
  background: #d4edda;
}

/* Estilos para las secciones del formulario */
.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.9rem;
}

/* Estilos para acordeones */
.accordion .btn {
  border-radius: 0.375rem;
  font-weight: 500;
  padding: 1rem 1.25rem;
  text-align: left;
  border: 1px solid #dee2e6;
  background-color: #fff;
  transition: all 0.15s ease-in-out;
}

.accordion .btn:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
}

.accordion .btn:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  border-color: #80bdff;
}

.accordion .btn[aria-expanded="true"] {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

.accordion .card-header {
  background-color: transparent;
  border-bottom: none;
  padding: 0;
}

.accordion .card-body {
  padding: 1.5rem;
  border-top: 1px solid #dee2e6;
}

/* Estilos para badges en acordeones */
.accordion-title-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Estilos específicos para los botones de acción en acordeones */
.accordion .btn .btn-sm {
  margin-left: auto;
  z-index: 10;
  position: relative;
}

@media (max-width: 768px) {
  .content-header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .content-header-right {
    margin-top: 1rem;
  }
  
  .timeline {
    padding-left: 1.5rem;
  }
  
  .timeline-item {
    padding-left: 1.5rem;
  }
  
  .timeline-marker {
    left: -1rem;
    width: 1.5rem;
    height: 1.5rem;
  }
  
  .timeline-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* Estilos para botones de acción de actividades */
.timeline-actions {
  display: flex;
  gap: 0.25rem;
}

.timeline-actions .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}

.timeline-actions .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
</style>

